import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import OpenAI from 'openai'

const inputSchema = z.object({
  name: z.string().min(1),
  colourHint: z.string().optional(),
})

export const suggestUserColours = createServerFn({ method: 'POST' })
  .inputValidator(inputSchema)
  .handler(async (ctx): Promise<{ colours: string[] }> => {
    const { name, colourHint } = ctx.data

    const apiKey = process.env.GROQ_API_KEY
    if (!apiKey) throw new Error('GROQ_API_KEY not set')

    const openai = new OpenAI({ apiKey, baseURL: 'https://api.groq.com/openai/v1' })

    const userPrompt = colourHint
      ? `Name: ${name}\nColour preference: ${colourHint}`
      : `Name: ${name}`

    const completion = await openai.chat.completions.create({
      model: 'llama-3.3-70b-versatile',
      messages: [
        {
          role: 'system',
          content:
            'You suggest 3 hex colour options for a user in a collaborative web application. The colours are used for text labels and small coloured dots next to the user\'s name, displayed on a white background. The 3 options should be similar shades of the same hue — like light, mid, and dark variants of one colour. All must be clearly readable as text on white (good contrast). Avoid anything too light, too grey, or near-black. Take the user\'s name and any colour preference into account. Return ONLY valid JSON: {"colours": ["#rrggbb", "#rrggbb", "#rrggbb"]}. Use lowercase hex.',
        },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.9,
      response_format: { type: 'json_object' },
    })

    const text = completion.choices[0]?.message?.content ?? '{}'
    const parsed = JSON.parse(text)
    const raw: unknown[] = Array.isArray(parsed.colours) ? parsed.colours : []
    const colours = raw
      .filter((c): c is string => typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c))
      .slice(0, 3)

    const fallbacks = ['#1d70b8', '#4c8cb8', '#1a5a8a']
    while (colours.length < 3) colours.push(fallbacks[colours.length])

    return { colours }
  })
